import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnDestroy, Signal, ViewChild } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatRow, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { first, Subject } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { GuestUserComponent } from 'src/app/components/project-settings/users/dialogs/guest-user/guest-user.component';
import { SelectUserComponent } from 'src/app/components/project-settings/users/dialogs/select-user/select-user.component';
import { Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { ButtonComponent, SpinnerComponent } from 'ui-library';

@Component({
    selector: 'app-users',
    imports: [
        ButtonComponent,
        MatPaginator,
        MatIcon,
        TranslateModule,
        MatToolbar,
        MatCheckboxModule,
        MatTableModule,
        MatRow,
        SpinnerComponent,
        CommonModule,
        NoDataComponent
    ],
    standalone: true,
    templateUrl: './users.component.html',
    styleUrl: './users.component.scss'
})

export class UsersComponent implements OnDestroy {
    // Services
    public dialog: MatDialog = inject(MatDialog);
    private projectService: ProjectService = inject(ProjectService);
    private userService: UserService = inject(UserService);
    private pop: Pop = inject(Pop);
    private translate: TranslateService = inject(TranslateService);

    // Component API
    @ViewChild('usersPaginator', { static: false }) usersPaginator: MatPaginator = new MatPaginator();
    @ViewChild(MatSort) sort: MatSort = new MatSort();

    // Variables
    usersCount: number = 0;
    mailingList: (User | string)[] = [];
    usersData: MatTableDataSource<any> = new MatTableDataSource();
    destroy$ = new Subject<void>();
    usersColumns: string[] = ['firstName', 'name', 'email', 'subscription', 'actions'];
    loading: Boolean = false;

    // Signals
    readonly project: Signal<Project> = this.projectService.currentProjectSignal;

    constructor() {
        effect(() => {
            const project = this.project();
            this.mailingList = project.mailingList ?? []
            this.loadListUsers()
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadListUsers() {
        this.loading = true;

        this.userService.getUsers({ projects: this.project().id }).pipe(first()).subscribe({
            next: (results: User[]) => {
                results.reverse();
                this.usersCount = results.length;

                results.forEach((user: User) => {
                    const isInMailingList = this.mailingList.some(item => {
                        if (typeof item === 'string') {
                            return item === user.id;
                        } else {
                            return item.id === user.id;
                        }
                    });

                    user.isInMailingList = isInMailingList;

                    let subscription;

                    if (user.subscriptions && user.subscriptions.length) {
                        subscription = user.subscriptions.find(s => s.project == this.project().id);
                    }

                    if (!subscription) {
                        user.subscription = {
                            project: this.project().id,
                            email: user.isInMailingList || false,
                            dailyReport: false,
                            weeklyReport: false,
                        }

                        if (user.settings && user.settings['subscribeToReport'] && user.subscription) {
                            if (user.settings['subscribeToReport'] == "daily")
                                user.subscription.dailyReport = true;

                            if (user.settings['subscribeToReport'] == "weekly")
                                user.subscription.weeklyReport = true;
                        }
                    } else {
                        user.subscription = subscription;
                    }
                })

                this.usersData.data = results;
                this.usersData.paginator = this.usersPaginator;
                this.loading = false;
            },
            error: (err) => {
                console.error('Error loading users:', err);
                this.loading = false;
            }
        });
    }

    selectUser() {
        const dialogRef = this.dialog.open(SelectUserComponent, {
            width: '80%',
            data: { project: this.project() }
        })

        dialogRef.afterClosed().subscribe(res => {
            this.loadListUsers();
        })
    }

    addGuestUser() {
        const dialog = this.dialog.open(GuestUserComponent, {
            width: '800px'
        })

        dialog.afterClosed().subscribe(res => {
            if (res.addGuestUser) this.loadListUsers();
        })
    }

    addUserToMailingList(user: User) {
        const userId = user.id;
        const searchId = this.mailingList.includes(userId);

        if (searchId) {
            const index = this.mailingList.indexOf(userId);
            this.mailingList.splice(index, 1);
            this.setUserSubscription(user, "email", { checked: false })
        } else {
            this.mailingList.push(userId);
            this.setUserSubscription(user, "email", { checked: true })
        }

        const json = {
            id: this.project().id,
            mailingList: this.mailingList
        }

        this.projectService.setProject(json).pipe(first()).subscribe({
            next: () => {
                this.project().mailingList = this.mailingList;
                this.projectService.setCurrentProject(this.project());
                this.pop.toast(this.translate.instant('users.mailing_list_success'), 'success', 4000, 'mail')
            },
            error: () => {
                this.pop.error(this.translate.instant('users.mailing_list_error'))
                console.error('Error updating mailing list');
            }
        })
    }

    setUserSubscription(user: User, type: 'email' | 'weeklyReport' | 'dailyReport', event: any) {
        if (user.subscription) {
            user.subscription[type] = event.checked;

            if (!user.subscriptions) {
                user.subscriptions = [{
                    ...user.subscription
                }];
            } else {
                let subIndex = user.subscriptions.findIndex(s => s.project == this.project().id);
                if (user.subscriptions[subIndex]) {
                    user.subscriptions[subIndex][type] = event.checked
                } else {
                    user.subscriptions.push({
                        ...user.subscription
                    })
                }
            }

            this.userService.setUser({ id: user.id, subscriptions: user.subscriptions }).pipe(first()).subscribe(() => { });
        }
    }

    deleteUser(user: User) {
        if (user.projects && user.projects.length) {
            let projectIndex = user.projects.findIndex(p => {
                if (typeof p === 'string') {
                    return p === this.project().id;
                } else {
                    return p.id === this.project().id;
                }
            });

            if (projectIndex >= 0) {
                user.projects.splice(projectIndex, 1);
                if (user.currentProject && user.currentProject == this.project().id) {
                    if (user.projects.length) {
                        user.currentProject = user.projects[0] ? (typeof user.projects[0] === 'string' ? user.projects[0] : (user.projects[0] as Project).id) : undefined;
                    }
                }
            }
        }

        const query = {
            id: user.id,
            currentProject: user.currentProject,
            projects: user.projects
        }

        this.userService.setUser(query).pipe(first()).subscribe(res => {
            this.projectService.updateUserAccess(user.id).pipe(first()).subscribe({
                next: () => {
                    this.loadListUsers();
                    this.pop.toast(this.translate.instant('users.delete_success'), 'success', 4000, 'delete')
                },
                error: (err) => {
                    console.error(err);
                    this.pop.toast(this.translate.instant('users.delete_error'), 'error', 4000, 'delete')
                }
            })
        });
    }

}
